import {modals} from '@mantine/modals';
import {useEffect, useRef, useState} from 'react';

import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import {supportsMSA, type ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import {RemainingExecutionsAndTenantNameQuery} from 'model-hub/scientific-models/structure-prediction/__generated/RemainingExecutionsAndTenantNameQuery';
import {
  filterModelsBySequenceLength,
  type InputComponent,
  isProteinInput,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
  requestRun,
} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import notifications from 'notifications';

import {
  looksLikeNucleotideOnlySequence,
  openDNASequenceWarningModal,
} from '../input-forms/structure-prediction/DNASequenceWarningModal';

export interface UseRunAllModelsInput {
  // Candidate models to run; each is length-filtered before submission.
  models: ReadonlyArray<ScientificModelNode>;
  // Whether the insufficient-credits toast should be surfaced (only from the list view).
  isListView: boolean;
  // The input component to run all models on.
  inputComponent: InputComponent;
  // Callback to refresh in progress predictions after submission. Omitted when the caller already
  // refreshes on its own (e.g. from the form view, where the form's onSubmit handles the refresh).
  onSubmitRunAllModels?: () => void;
  // Identifier of the currently viewed version. When it changes, any open run-all DNA warning modal is
  // dismissed so a stale warning does not linger after the user navigates to a different version.
  versionId: string | number;
}

export interface UseRunAllModelsResult {
  handleRunAllModels: () => Promise<boolean>;
  submitting: boolean;
}

function expandInputComponent(inputComponent: InputComponent): {
  proteinIds: Array<string>;
  moleculeIds: Array<string>;
  aaSequenceIds: Array<string>;
  primaryComponentId: string;
  folderId: string;
  aminoAcids: string;
} {
  if (isProteinInput(inputComponent)) {
    return {
      proteinIds: [inputComponent.id],
      moleculeIds: [],
      aaSequenceIds: [],
      primaryComponentId: inputComponent.id,
      folderId: inputComponent.folderId,
      aminoAcids: inputComponent.chains.join(''),
    };
  } else {
    return {
      proteinIds: [],
      moleculeIds: [],
      aaSequenceIds: [inputComponent.id],
      primaryComponentId: inputComponent.id,
      folderId: inputComponent.folderId,
      aminoAcids: inputComponent.aminoAcids,
    };
  }
}

/**
 * Owns the "run all models" orchestration shared by the structure-prediction tab views: the in-flight
 * guard, the submitting flag, length filtering, and the queue/notify/credits-error handling. Models are
 * submitted with `Promise.all`, so any submission failure aborts the batch and rejects, surfacing the
 * insufficient-credits toast from the list view or a generic error otherwise.
 */
export function useRunAllModels({
  models,
  isListView,
  inputComponent,
  onSubmitRunAllModels,
  versionId,
}: UseRunAllModelsInput): UseRunAllModelsResult {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const inFlightRef = useRef<boolean>(false);
  const {proteinIds, moleculeIds, aaSequenceIds, primaryComponentId, folderId, aminoAcids} =
    expandInputComponent(inputComponent);
  const runAllWarningModalId = `run-all-dna-warning-${primaryComponentId}`;
  const remainingExecutionsQueryResult = useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(
    REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY
  );

  // Dismiss any lingering run-all DNA warning modal when the viewed version changes.
  useEffect(() => {
    modals.close(runAllWarningModalId);
  }, [versionId, runAllWarningModalId]);

  const execute = async (): Promise<void> => {
    if (remainingExecutionsQueryResult.type !== QueryResultType.SUCCESS) {
      notifications.error('Unable to queue predictions at this time');
      throw new Error('Unable to queue predictions');
    }

    const {validModels, invalidModelNames} = filterModelsBySequenceLength(models, aminoAcids.length);

    try {
      await Promise.all(
        validModels.map((model) =>
          requestRun({
            aaSequenceIds,
            moleculeIds,
            proteinIds,
            primaryComponentId,
            // Only the protein-entity flow carries a string version id (`cpstvrsn_...`), which is the
            // key for `proteinVersionPredictionJobs` eviction; the aa-sequence flow's numeric db id is not.
            primaryComponentVersionId: typeof versionId === 'string' ? versionId : undefined,
            folderId,
            remainingExecutionsAndTenantNameData: remainingExecutionsQueryResult.data,
            selectedModel: model,
            msaEnabled: supportsMSA(model),
            source: MODEL_RUN_SOURCE.STRUCTURE_TAB,
          })
        )
      );
      if (invalidModelNames.length > 0) {
        notifications.warn(
          `The following models were not queued because they are not supported for this sequence length: ${invalidModelNames.join(', ')}`
        );
        notifications.success(`Successfully queued ${validModels.length} prediction models`);
      } else {
        notifications.success('Successfully queued all prediction models');
      }
      onSubmitRunAllModels?.();
    } catch (error) {
      const creditsError = getInsufficientCreditsAgentTypeError(error);
      if (creditsError) {
        if (isListView) {
          notifications.error(`${creditsError.title} ${creditsError.detail}`);
        }
        throw error;
      }
      notifications.error('Failed to queue one or more prediction models');
      throw error;
    }
  };

  const guardedExecuteRunAllModels = async (): Promise<boolean> => {
    if (inFlightRef.current) {
      return false;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    try {
      await execute();
      return true;
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const handleRunAllModels = async (): Promise<boolean> => {
    if (inFlightRef.current) {
      return false;
    }
    if (looksLikeNucleotideOnlySequence(aminoAcids)) {
      const shouldProceedWithSubmission = await openDNASequenceWarningModal({modalId: runAllWarningModalId});
      if (!shouldProceedWithSubmission || inFlightRef.current) {
        return false;
      }
    }
    return await guardedExecuteRunAllModels();
  };

  return {handleRunAllModels, submitting};
}
